import React from 'react';
import { Box, BoxProps } from '@mui/material';

export interface SavedWorkActionRowProps extends Omit<BoxProps, 'children'> {
  /** The primary action remains centered in the available width. */
  action: React.ReactNode;
  /** Existing checkbox/label, including its current state and handlers. */
  savedWork: React.ReactNode;
  /** Optional control, such as a switch to manual entry, on the left. */
  leading?: React.ReactNode;
  /** Give a long secondary control its own row on narrow screens. */
  stackLeadingOnMobile?: boolean;
}

/** Align a workflow action and its saved-work choice without adding a second row. */
export function SavedWorkActionRow({ action, savedWork, leading, stackLeadingOnMobile, sx, ...boxProps }: SavedWorkActionRowProps) {
  const stackLeading = Boolean(stackLeadingOnMobile && leading);
  return (
    <Box
      data-testid="saved-work-action-row"
      data-layout="centered-action"
      {...boxProps}
      sx={[
        {
          display: 'grid',
          width: '100%',
          gridTemplateColumns: {
            xs: 'minmax(70px, 1fr) minmax(0, max-content) minmax(70px, 1fr)',
            sm: 'minmax(0, 1fr) auto minmax(0, 1fr)',
          },
          alignItems: 'center',
          columnGap: 0.5,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box
        data-testid="saved-work-action-leading"
        sx={{
          minWidth: 0,
          justifySelf: { xs: stackLeading ? 'center' : 'start', sm: 'start' },
          gridColumn: { xs: stackLeading ? '1 / -1' : 'auto', sm: 'auto' },
          gridRow: { xs: stackLeading ? '1' : 'auto', sm: 'auto' },
          maxWidth: '100%',
          '& .MuiButton-root': { maxWidth: '100%', whiteSpace: 'normal' },
          '@media (max-width:599.95px)': {
            '& .MuiButton-root': { px: 0.25, lineHeight: 1.2 },
            '& .MuiButton-startIcon': { m: 0 },
            '& .MuiButton-startIcon > *:first-of-type': { fontSize: 16 },
          },
        }}
      >
        {leading}
      </Box>
      <Box
        data-testid="saved-work-action-button"
        sx={{
          minWidth: 0,
          justifySelf: 'center',
          gridColumn: { xs: stackLeading ? '2' : 'auto', sm: 'auto' },
          gridRow: { xs: stackLeading ? '2' : 'auto', sm: 'auto' },
          maxWidth: '100%',
          '& .MuiButton-root': { maxWidth: '100%', whiteSpace: 'normal' },
          '@media (max-width:599.95px)': {
            '& .MuiButton-root': { px: 0.375, lineHeight: 1.2 },
            '& .MuiButton-startIcon': { ml: 0, mr: 0 },
            '& .MuiButton-startIcon > *:first-of-type': { fontSize: 16 },
          },
        }}
      >
        {action}
      </Box>
      <Box
        data-testid="saved-work-action-toggle"
        sx={{
          minWidth: 0,
          maxWidth: '100%',
          justifySelf: 'start',
          gridColumn: { xs: stackLeading ? '3' : 'auto', sm: 'auto' },
          gridRow: { xs: stackLeading ? '2' : 'auto', sm: 'auto' },
          '& .MuiFormControlLabel-root': { m: 0, minHeight: 44, minWidth: 0, maxWidth: '100%' },
          // The label remains the 44px click target. On phones the icon itself
          // can use less space, leaving enough room for words to wrap cleanly.
          '& .MuiCheckbox-root': { p: { xs: 0, sm: '9px' }, width: { xs: 24, sm: 42 }, height: { xs: 24, sm: 42 }, minWidth: { xs: 24, sm: 44 }, minHeight: { xs: 24, sm: 44 }, flexShrink: 0 },
          '& .MuiFormControlLabel-label': { minWidth: 0, overflowWrap: 'anywhere' },
        }}
      >
        {savedWork}
      </Box>
    </Box>
  );
}

export default SavedWorkActionRow;
